import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Truck } from "react-feather";

const AddEdit_VehicleMaster = () => {
  const API_URL_SAVE = "VehicleMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleMaster/Id";
  const API_URL_VEHICLE_TYPE = `${API_WEB_URLS.MASTER}/0/token/VehicleTypeMaster/Id/0`;
  
  const [state, setState] = useState({
    id: 0,
    formData: { 
      F_VehicleType: "",
      Name: "",
      VehicleRegistrationNo: "",
      VehicleNo: "",
      WeightCapacity: "",
    }
  });

  const [vehicleTypeList, setVehicleTypeList] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Fetch Vehicle Type list
  useEffect(() => {
    Fn_FillListData(dispatch, setVehicleTypeList, "gridData", API_URL_VEHICLE_TYPE);
  }, [dispatch]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    F_VehicleType: Yup.string()
      .required("Vehicle type is required"),
    Name: Yup.string()
      .required("Vehicle name is required")
      .min(2, "Vehicle name must be at least 2 characters")
      .max(100, "Vehicle name must be less than 100 characters"),
    VehicleRegistrationNo: Yup.string()
      .required("Vehicle registration number is required")
      .min(2, "Vehicle registration number must be at least 2 characters")
      .max(100, "Vehicle registration number must be less than 100 characters"),
    VehicleNo: Yup.string()
      .required("Vehicle number is required")
      .min(2, "Vehicle number must be at least 2 characters")
      .max(100, "Vehicle number must be less than 100 characters"),
    WeightCapacity: Yup.number()
      .required("Weight capacity is required")
      .min(0, "Weight capacity must be 0 or greater")
      .max(999999.99, "Weight capacity must be less than 999,999.99"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("F_VehicleType", values.F_VehicleType || "");
    formData.append("Name", values.Name || "");
    formData.append("VehicleRegistrationNo", values.VehicleRegistrationNo || "");
    formData.append("VehicleNo", values.VehicleNo || "");
    formData.append("WeightCapacity", values.WeightCapacity || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehicleid",
      navigate,
      "/VehicleMaster"
    );
  };

  return (
    <div style={{ padding: "6px 8px", maxWidth: 900, margin: "0 auto" }}>
      <Card className="border shadow-none" style={{ marginBottom: 0 }}>
        <CardBody className="p-2" style={{ padding: "8px 12px !important" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #dee2e6" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Vehicle Master</span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button type="button" size="sm" color="light" className="py-1 px-2" style={{ fontSize: 12 }} onClick={() => navigate("/VehicleMaster")}><ArrowLeft size={12} className="me-1" />Back</Button>
              <Button type="submit" form="vehicle-form" size="sm" color="primary" className="py-1 px-2" style={{ fontSize: 12 }}><Save size={12} className="me-1" />Save</Button>
            </div>
          </div>
          <Formik 
                  initialValues={{
                    F_VehicleType: state.formData.F_VehicleType || "",
                    Name: state.formData.Name || "",
                    VehicleRegistrationNo: state.formData.VehicleRegistrationNo || "",
                    VehicleNo: state.formData.VehicleNo || "",
                    WeightCapacity: state.formData.WeightCapacity || "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form id="vehicle-form">
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="F_VehicleType" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Type <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_VehicleType && touched.F_VehicleType ? 'is-invalid' : ''}`}
                              name="F_VehicleType"
                            >
                              <option value="">Select Vehicle Type</option>
                              {vehicleTypeList && vehicleTypeList.map((type) => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_VehicleType" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select the type of vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Name" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={nameRef}
                              className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                              type="text"
                              name="Name"
                              placeholder="Enter vehicle name (e.g., Swift, Innova)"
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Name" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the name of the vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="VehicleRegistrationNo" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Registration No <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.VehicleRegistrationNo && touched.VehicleRegistrationNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="VehicleRegistrationNo"
                              placeholder="Enter registration number (e.g., MH-01-AB-1234)"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="VehicleRegistrationNo" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the official registration number of the vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="VehicleNo" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Number <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.VehicleNo && touched.VehicleNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="VehicleNo"
                              placeholder="Enter vehicle number"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="VehicleNo" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the vehicle number
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="WeightCapacity" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Weight Capacity (Kg) <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.WeightCapacity && touched.WeightCapacity ? 'is-invalid' : ''}`}
                              type="number"
                              name="WeightCapacity"
                              placeholder="Enter weight capacity in kg"
                              step="0.01"
                              min="0"
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="WeightCapacity" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the maximum weight capacity in kilograms
                          </div>
                        </div>
                      </div>

                    </Form>
                  )}
                </Formik>
        </CardBody>
      </Card>
    </div>
  );
};

export default AddEdit_VehicleMaster;


